// hooks/usePreguntasRespuestas.js
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

// ---- util: barajar (Fisher–Yates, sin mutar) ----
function shuffleArray(arr = []) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const usePreguntasRespuestas = (nivelId) => {
  // Estados para manejar las preguntas, respuestas y el flujo del juego
  const [preguntas, setPreguntas] = useState([]);
  const [preguntaActual, setPreguntaActual] = useState(null);
  const [preguntaIndex, setPreguntaIndex] = useState(0);
  const [respuestaSeleccionada, setRespuestaSeleccionada] = useState(null);
  const [nivelCompletado, setNivelCompletado] = useState(false);
  const [tiempoRestante, setTiempoRestante] = useState(null);
  const [respuestas, setRespuestas] = useState([]);
  const [tiempoAgotadoFlag, setTiempoAgotadoFlag] = useState(false);
  const [mostrarAlerta, setMostrarAlerta] = useState(false);
  const [alertaInfo, setAlertaInfo] = useState({
    esCorrecta: false,
    mensaje: "",
  });
  const [retroalimentacion, setRetroalimentacion] = useState("");
  const [mensaje, setMensaje] = useState(null);
  const [mostrarResultado, setMostrarResultado] = useState(false);

  const navigate = useNavigate();

  // Ref y helper para pausar el temporizador en cualquier momento
  const timerRef = useRef(null);
  const pauseTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  function formatTiempo(segundos) {
    const minutos = Math.floor(segundos / 60);
    const segundosRestantes = segundos % 60;

    const minutosFormateados = minutos.toString().padStart(2, "0");
    const segundosFormateados = segundosRestantes.toString().padStart(2, "0");

    return `${minutosFormateados}:${segundosFormateados}`;
  }

  // Efecto para cargar preguntas al cambiar el nivel
  useEffect(() => {
    cargarPreguntas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nivelId]);

  // Efecto para manejar el temporizador (pausa con alerta, agotado o nivel completado)
  useEffect(() => {
    const debeCorrer =
      tiempoRestante !== null &&
      tiempoRestante > 0 &&
      !mostrarAlerta &&
      !tiempoAgotadoFlag &&
      !nivelCompletado;

    if (debeCorrer) {
      timerRef.current = setInterval(() => {
        setTiempoRestante((prev) => {
          if (prev <= 1) {
            pauseTimer();
            setTiempoAgotadoFlag(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      // Si no debe correr por cualquier motivo, asegúrate de pausar
      pauseTimer();
    }

    return () => {
      pauseTimer();
    };
  }, [tiempoRestante, mostrarAlerta, tiempoAgotadoFlag, nivelCompletado]);

  // Efecto para manejar cuando se agota el tiempo
  useEffect(() => {
    if (tiempoAgotadoFlag) {
      tiempoAgotado();
    }
  }, [tiempoAgotadoFlag]);

  // Función para cargar preguntas desde la API (orden de PREGUNTAS intacto)
  const cargarPreguntas = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/api/preguntas/tema/${nivelId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = Array.isArray(response.data) ? response.data : [];

      setPreguntas(data);

      // Pregunta inicial con RESPUESTAS barajadas
      const first = data[0];
      const respuestasValidas = (first?.respuestas || []).filter(
        (r) => (r.texto_respuesta || "").trim() !== ""
      );
      setPreguntaActual(
        first ? { ...first, respuestas: shuffleArray(respuestasValidas) } : null
      );

      setPreguntaIndex(0);
      setNivelCompletado(false);
      if (tiempoRestante === null) {
        setTiempoRestante(first?.limite_tiempo ?? 60);
      }
      setRespuestas([]);
      setTiempoAgotadoFlag(false);
    } catch (error) {
      console.error("Error al cargar preguntas:", error);
    }
  };

  // Función para manejar la selección de respuesta
  const handleRespuesta = async (respuestaId) => {
    // Pausa inmediatamente para que no siga el conteo mientras llega/verifica y aparece la alerta
    pauseTimer();

    setRespuestaSeleccionada(respuestaId);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_BASE_URL}/api/preguntas/verificar`,
        { pregunta_id: preguntaActual.id, respuesta_id: respuestaId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const nuevaRespuesta = {
        preguntaActual: preguntaActual.id,
        esCorrecta: response.data.es_correcta,
      };

      setRetroalimentacion(response.data.retroalimentacion);
      setRespuestas((prevRespuestas) => {
        const yaExiste = prevRespuestas.some(
          (r) =>
            r.preguntaActual === response.data.pregunta_id &&
            response.data.es_correcta == "0"
        );
        let updatedRespuestas;

        if (!yaExiste) {
          updatedRespuestas = [...prevRespuestas, nuevaRespuesta];
        } else {
          updatedRespuestas = [...prevRespuestas];
        }

        // Ajuste de tiempo y alerta
        if (response.data.es_correcta) {
          setTiempoRestante((prev) => prev + 10);
          setAlertaInfo({ esCorrecta: true, mensaje: "+10 Seg" });
        } else {
          const nuevoTiempo = Math.max(0, tiempoRestante - 5);
          setTiempoRestante(nuevoTiempo);
          if (nuevoTiempo === 0) {
            setTiempoAgotadoFlag(true);
          } else {
            setAlertaInfo({ esCorrecta: false, mensaje: "-5 Seg" });
          }
        }

        setMostrarAlerta(true);
        return updatedRespuestas;
      });

      if (!response.data.es_correcta) {
        return;
      }
    } catch (error) {
      console.error("Error al verificar respuesta:", error);
    }
  };

  // Función para avanzar a la siguiente pregunta (RESPUESTAS barajadas al mostrar)
  const avanzarPregunta = () => {
    const siguientePreguntaIndex = preguntaIndex + 1;
    if (siguientePreguntaIndex < preguntas.length) {
      setPreguntaIndex(siguientePreguntaIndex);

      const nextQ = preguntas[siguientePreguntaIndex];
      const respuestasValidas = (nextQ.respuestas || []).filter(
        (r) => (r.texto_respuesta || "").trim() !== ""
      );
      setPreguntaActual({
        ...nextQ,
        respuestas: shuffleArray(respuestasValidas),
      });

      setRespuestaSeleccionada(null);
    } else {
      finalizarNivel();
    }
  };

  // Función para manejar cuando se agota el tiempo
  const tiempoAgotado = () => {
    setMostrarAlerta(true);
    setAlertaInfo({
      esCorrecta: false,
      mensaje: "Tiempo Agotado",
      imagen: "timerOut",
      gif: "RelojArena",
    });
  };

  // Función para finalizar el nivel y guardar resultados
  const finalizarNivel = async () => {
    setNivelCompletado(true);

    // guardar progreso (usando tema_id, no nivel_id)
    await actualizarNivelUsuario();

    const respuestasCorrectas = respuestas.filter((r) => r.esCorrecta).length;
    const respuestasIncorrectas = respuestas.filter(
      (r) => !r.esCorrecta
    ).length;

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/api/resultados`,
        {
          tema_id: nivelId, // nivelId aquí en realidad es tema_id
          respuestas_correctas: respuestasCorrectas,
          respuestas_incorrectas: respuestasIncorrectas,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error("Error al guardar resultados:", error);
    }

    setMensaje({
      respuestasCorrectas,
      respuestasIncorrectas,
    });
  };

  // Función para actualizar el nivel del usuario en la base de datos
  const actualizarNivelUsuario = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/api/niveles/usuario/actualizar`,
        { tema_id: nivelId }, // <-- usamos tema_id
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error("Error al actualizar nivel del usuario:", error);
    }
  };

  // Retorna los estados y funciones necesarios para el componente que use este hook
  return {
    preguntaActual,
    tiempoRestante,
    nivelCompletado,
    tiempoAgotadoFlag,
    mostrarAlerta,
    alertaInfo,
    retroalimentacion,
    mensaje,
    mostrarResultado,
    formatTiempo,
    handleRespuesta,
    avanzarPregunta,
    setMostrarAlerta,
    finalizarNivel,
    setMostrarResultado,
  };
};

export default usePreguntasRespuestas;
